import React from 'react';
import { toast } from 'react-toastify';
import { Menu } from '@mui/material';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import { ArcIconButton, ArcMenuItem, ArcOutlinedSelect, ArcPagination, ArcSkeleton, ArcStack, ArcTable, ArcTableBody, ArcTableCell, ArcTableHead, ArcTableRow, ArcText } from '@src/core/ui';
import { useDeleteUserMutation, useGetAllRolesQuery, useGetUsersQuery, useSetUserRoleMutation } from '@src/features/settings/redux';
import { CreateUserForm, ResetPasswordForm } from '@src/features/settings/pages/settings-page/components';
import { useGetUserDataQuery, UserEntity, UserRoleType } from '@src/core/features';
import { UserEntity as SettingsUserEntity } from '@src/features/settings/entities';
import { useConfirm } from '@src/core/controllers';
import { trackError, toTrackedError } from '@src/core/sentry';
import { PAGINATION_DEFAULT_SIZE, ZERO } from '@src/core/config';

const UserRowActions: React.FC<{
  user: SettingsUserEntity;
  canResetPassword: boolean;
  canDelete: boolean;
  isDeleting: boolean;
  onDelete: () => void;
}> = ({ user, canResetPassword, canDelete, isDeleting, onDelete }) => {
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const [isResetOpen, setIsResetOpen] = React.useState(false);

  const closeMenu = () => setAnchorEl(null);

  if (!canResetPassword && !canDelete) return null;

  return (
    <>
      <ArcIconButton
        aria-label='User actions'
        aria-haspopup='true'
        aria-expanded={Boolean(anchorEl)}
        onClick={(event) => setAnchorEl(event.currentTarget)}>
        <MoreVertIcon />
      </ArcIconButton>

      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={closeMenu}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        slotProps={{ paper: { sx: { minWidth: 180 } } }}>
        {canResetPassword && (
          <ArcMenuItem
            onClick={() => {
              closeMenu();
              setIsResetOpen(true);
            }}>
            Reset password
          </ArcMenuItem>
        )}
        {canDelete && (
          <ArcMenuItem
            disabled={isDeleting}
            sx={{ color: 'error.main' }}
            onClick={() => {
              closeMenu();
              onDelete();
            }}>
            Delete
          </ArcMenuItem>
        )}
      </Menu>

      {canResetPassword && (
        <ResetPasswordForm user={user} open={isResetOpen} onClose={() => setIsResetOpen(false)} />
      )}
    </>
  );
};

const UserPanel: React.FC = () => {
  const { data = {} as UserEntity } = useGetUserDataQuery();
  const [page, setPage] = React.useState(0);
  const { data: users = { data: [], totalCount: ZERO }, isLoading: isUsersLoading } = useGetUsersQuery({
    page,
    size: PAGINATION_DEFAULT_SIZE,
  });
  const { data: roles = [], isLoading: isRolesLoading } = useGetAllRolesQuery();
  const { confirm } = useConfirm()  ;

  const [setUserRole, { isLoading: isSubmitting }] = useSetUserRoleMutation();
  const [deleteUser, { isLoading: isDeleting }] = useDeleteUserMutation();

  const pageCount = Math.max(1, Math.ceil(users.totalCount / PAGINATION_DEFAULT_SIZE));

  React.useEffect(() => {
    if (page >= pageCount) setPage(Math.max(0, pageCount - 1));
  }, [page, pageCount]);

  const onSetUserRole = (id: number, role: string) => {
    setUserRole({ id, role }).unwrap().then(() => {
      toast.success('SET USER ROLE SUCCESS TEXT');
    }).catch(() => {
      toast.error('Failed to set user role');
    });
  };

  const onDeleteUser = async (userId: number) => {
    if (userId === data.id) {
      toast.error('Cannot delete yourself');
      return;
    }

    const userToDelete = users?.data?.find(u => u.id === userId);
    if (userToDelete?.role === UserRoleType.OWNER) {
      toast.error('Cannot delete OWNER');
      return;
    }

    const confirmed = await confirm(
      `This action cannot be undone. The user’s account and all associated data will be permanently removed.`,
      'Delete User?'
    );

    if (!confirmed) {
      return;
    }

    deleteUser(userId).unwrap()
      .then(() => {
        toast.success('User deleted successfully');
      })
      .catch((error: unknown) => {
        trackError(toTrackedError(error), { code: 'FE_0018', extra: { action: 'deleteUser', userId } });
        const errorMessage =
          (error as { data?: { message?: string }; message?: string })?.data?.message ||
          (error as { message?: string })?.message ||
          'Failed to delete user';
        toast.error(errorMessage);
      });
  };

  if (isUsersLoading || isRolesLoading) {
    return (
      <ArcStack spacing={1}>
        <ArcSkeleton height={50} />
        <ArcSkeleton height={50} />
        <ArcSkeleton height={50} />
        <ArcSkeleton height={50} />
        <ArcSkeleton height={50} />
      </ArcStack>
    );
  }


  return (
    <ArcStack p={2}>
      <CreateUserForm />
      <ArcTable>
        <ArcTableHead>
          <ArcTableRow>
            <ArcTableCell sx={{ fontWeight: 600, fontSize: 12, border: 0 }}> FULL NAME </ArcTableCell>
            <ArcTableCell sx={{ fontWeight: 600, fontSize: 12, border: 0 }}> EMAIL </ArcTableCell>
            <ArcTableCell sx={{ fontWeight: 600, fontSize: 12, border: 0 }}> STATUS </ArcTableCell>
            <ArcTableCell sx={{ fontWeight: 600, fontSize: 12, border: 0 }}> ROLE </ArcTableCell>
            <ArcTableCell sx={{ fontWeight: 600, fontSize: 12, border: 0 }} />
          </ArcTableRow>
        </ArcTableHead>

        <ArcTableBody>
          {users.data.map((user) => { 
            const isDisabled =
              isSubmitting ||
              user.id === data.id ||
              user.role === UserRoleType.SUPERADMIN ||
              (user.role === UserRoleType.OWNER && data.role !== UserRoleType.OWNER);
            const canResetPassword =
              user.role !== UserRoleType.SUPERADMIN &&
              (user.role !== UserRoleType.OWNER || data.role === UserRoleType.OWNER);

            const canDelete =
              user.id !== data.id && !isDisabled && user.role !== UserRoleType.OWNER;

            return (
              <ArcTableRow key={user.id}>
                <ArcTableCell sx={{ border: 0 }}> {user.firstname} {user.lastname} </ArcTableCell>
                <ArcTableCell sx={{ border: 0 }}> {user.email} </ArcTableCell>
                <ArcTableCell sx={{ border: 0 }}>
                  <ArcText
                    fontSize={14}
                    color={user.activated ? 'success.main' : 'warning.main'}>
                    {user.activated ? 'Active' : 'Invited'}
                  </ArcText>
                </ArcTableCell>
                { <ArcTableCell sx={{ border: 0, width: 250 }}> 
                  <ArcOutlinedSelect 
                    sx={{ 
                      width: 250,
                      '& .MuiSelect-select.Mui-disabled': {
                        color: (theme) => theme.palette.text.secondary,
                        WebkitTextFillColor: (theme) => theme.palette.text.secondary,
                        opacity: 1,
                      },
                      '& .MuiOutlinedInput-notchedOutline': {
                        borderColor: (theme) => 
                          (isDisabled) 
                            ? theme.palette.action.disabled 
                            : 'white',
                      },
                    }}
                    value={user.role} 
                    disabled={isDisabled} 
                    onChange={(e) => onSetUserRole(+user.id, e.target.value as string)}>
                    {roles
                      .filter((role) => {
                        if (role === UserRoleType.SUPERADMIN)
                          return role === user.role;
                        if (role === UserRoleType.OWNER)
                          return (
                            role === user.role || data.role === UserRoleType.OWNER
                          );
                        return true;
                      })
                      .map((role) => (
                        <ArcMenuItem key={role} value={role}>
                          {role}
                        </ArcMenuItem>
                      ))}
                  </ArcOutlinedSelect>
                </ArcTableCell>}
                <ArcTableCell sx={{ border: 0, width: 100 }}> 
                  <UserRowActions
                    user={user}
                    canResetPassword={canResetPassword}
                    canDelete={canDelete}
                    isDeleting={isDeleting}
                    onDelete={() => onDeleteUser(user.id)} />
                </ArcTableCell>
 
              </ArcTableRow>
            );})}
        </ArcTableBody>
      </ArcTable>

      {PAGINATION_DEFAULT_SIZE < users.totalCount && (
        <ArcStack alignItems='center' pt={2}>
          <ArcPagination
            count={pageCount}
            page={page + 1}
            onChange={(_event, value) => setPage(value - 1)} />
        </ArcStack>
      )}
    </ArcStack>
  );
};

export default UserPanel;